import { Component, ReactNode } from 'react';
import { reportError } from '@/lib/errorReporter';
import { ErrorBoundaryFallback } from './ErrorBoundaryFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  name?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string;
}

function generateErrorId(): string {
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `err_${ts}_${rand}`;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, errorId: '' };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const errorId = generateErrorId();
    this.setState({ errorId });
    const componentName = this.props.name || 'unknown';
    console.error(`[ErrorBoundary:${componentName}]`, errorId, error, info.componentStack);
    reportError(error, {
      component: `ErrorBoundary:${componentName}`,
      componentStack: info.componentStack,
      errorId,
    });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorId: '' });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <ErrorBoundaryFallback
          error={this.state.error}
          errorId={this.state.errorId}
          componentName={this.props.name || 'unknown'}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}
